<template>
    <ul class="list-unstyled mb-0 w-100 d-flex flex-column align-items-end" style="padding-right: 0;">
        <li class="msg_wpp_render right sent-message w-100 d-flex justify-content-end">
            <div class="conversation-list">
                <div class="ctext-wrap">
                    <div class="ctext-wrap-content shadow-sm" style="padding: 10px; max-width: 320px;">

                        <div>
                            <template v-if="temCabecalho">
                                <h6 v-if="headerType === 'text'" class="template-header mb-2">{{ headerText }}</h6>

                                <div v-else>
                                    <template v-if="['JPG', 'PNG', 'JPEG', undefined].includes(headerFileMime)">
                                        <div class="image-message cursor-pointer position-relative">
                                            <img :src="headerFileUrl" class="img-fluid rounded mb-1 chat-media-fixed" alt="Imagem">
                                        </div>
                                    </template>
                                    <template v-else-if="headerFileMime === 'MP4'">
                                        <div class="image-message position-relative">
                                            <video v-liberar-midia
                                                :src="headerFileUrl"
                                                class="img-fluid rounded mb-1"
                                                controls
                                                preload="metadata"
                                                style="max-height: 300px; width: 100%; object-fit: contain; background-color: rgba(0,0,0,0.1);"
                                            ></video>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="file-message-card position-relative w-100" style="min-width: 250px;">
                                            <div class="file-card-inner">
                                                <div class="file-icon"><i class="ri-file-text-fill"></i></div>
                                                <div class="file-info">
                                                    <span class="file-name text-truncate">{{ headerFileName || 'DocumentoExemplo.pdf' }}</span>
                                                    <span class="file-meta">Template em Anexo</span>
                                                </div>
                                                <button class="btn-download-file" type="button" disabled>
                                                    <i class="ri-download-2-line"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </template>

                            <p class="mb-0 message-text" style="white-space: pre-wrap;">{{ bodyText || textoVazio }}</p>
                            <p v-if="footerText" class="template-footer mt-2">{{ footerText }}</p>
                        </div>

                        <div class="chat-time mt-1 text-end text-muted font-size-11">
                            <i class="ri-check-double-line text-info"></i> Agora
                        </div>

                        <div v-if="buttons.length" class="template-buttons-preview">
                            <div v-for="(pb, i) of buttons" :key="`pbtn-${i}`" class="template-btn-preview">
                                <i :class="pb.icon" class="me-1"></i> {{ pb.label }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </li>
    </ul>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';

/**
 * Bolha de pré-visualização de um template Meta, como o cliente a vê.
 *
 * Extraída da marcação inline de `MetaWhatsappTemplateForm.vue`, que era o único
 * lugar com ela. A tela de PREENCHIMENTO de variáveis precisa da mesma bolha, e
 * duplicá-la significaria duas versões do que o cliente enxerga — divergindo
 * exatamente onde o operador confia no que está vendo.
 *
 * Componente burro de propósito: quem chama decide se o texto traz valores
 * digitados, valores de exemplo, ou os `{{...}}` crus.
 */
export interface BotaoPreview {
    icon: string;
    label: string;
}

const props = defineProps({
    /** `'text'` desenha um cabeçalho de texto; `'file'` desenha mídia. Ausente = sem cabeçalho. */
    headerType: { type: String as PropType<'text' | 'file' | null>, default: null },
    headerText: { type: String, default: '' },
    /** `JPG`/`PNG`/`JPEG` (ou ausente) desenha imagem, `MP4` desenha vídeo, o resto vira card de arquivo. */
    headerFileMime: { type: String as PropType<string | undefined>, default: undefined },
    headerFileUrl: { type: String, default: '' },
    headerFileName: { type: String, default: '' },
    bodyText: { type: String, default: '' },
    footerText: { type: String, default: '' },
    buttons: { type: Array as PropType<BotaoPreview[]>, default: () => [] },
    /** Texto mostrado enquanto o corpo está vazio. */
    textoVazio: { type: String, default: 'A mensagem do template aparecerá aqui...' },
});

const temCabecalho = computed(() => !!props.headerType);
</script>

<style lang="css" scoped>
/*
 * Movido de `MetaWhatsappTemplateForm.vue`, cujo `<style>` é `scoped` — as regras
 * não alcançariam este componente, e a bolha sairia sem cabeçalho em negrito e com
 * os botões colados no texto.
 *
 * O rodapé NÃO leva `text-transform: uppercase`: o WhatsApp entrega o rodapé com a
 * caixa exatamente como ela foi aprovada, e a pré-visualização em caixa alta fazia
 * o operador revisar um texto que o cliente nunca vê.
 */
.template-header { font-weight: 700; color: #333; margin: 0; font-size: 16px; }
.template-footer { margin-top: 16px; font-size: 10px; color: #aaa; letter-spacing: 0.5px; }

/* Botões do template na pré-visualização (estilo WhatsApp) */
.template-buttons-preview {
    margin: 6px -10px -10px -10px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.template-btn-preview {
    display: flex; align-items: center; justify-content: center;
    padding: 9px 12px; font-size: 14px; font-weight: 500;
    color: #00a5f4; cursor: pointer;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.template-btn-preview:last-child { border-bottom: none; }
[data-bs-theme="dark"] .template-buttons-preview { border-top-color: rgba(255, 255, 255, 0.12); }
[data-bs-theme="dark"] .template-btn-preview { border-bottom-color: rgba(255, 255, 255, 0.08); }
</style>
